import React, { useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import ClickableStack from 'talent_matching/components/shared/clickable_stack';
import Container from '@grnhse/seedling/lib/birch/components/container';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import { Body, Label } from '@grnhse/seedling/lib/birch/typography';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import AddInCircleIcon from '@grnhse/seedling/lib/birch/icons/add_in_circle';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/arrow_up';
import { Skill } from 'talent_matching/types';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import { useEffect } from 'react';

const t = withLocalePrefix(
  'talent_matching.modals.calibration.calibrate_skills'
);

const StyledContainer = styled(Container)`
  border-color: ${Tokens.colors.selected};
`;

const SkillButtons = ({
  remainingSkills,
  label,
}: {
  remainingSkills: Skill[];
  label: string;
}) => {
  const { watch, setValue } = useFormContext();
  const selectedSkills = watch('skills');

  if (remainingSkills.length === 0) {
    return null;
  }

  const onAddSkill = (skill: Skill) => {
    setValue('skills', [...selectedSkills, skill], { shouldDirty: true });
  };

  return (
    <>
      <Label>{label}</Label>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        {remainingSkills.map((skill: Skill, idx: number) => (
          <SecondaryButton
            key={`${idx}-${skill.name}`}
            icon={AddInCircleIcon}
            onClick={() => onAddSkill(skill)}
            size="sm"
          >
            {skill.name}
          </SecondaryButton>
        ))}
      </Stack>
    </>
  );
};

type Props = {
  errorMessage?: string;
};

const ErrorText = styled(Body)`
  color: ${Tokens.colors.error};
  margin-top: ${Tokens.spacing['0.5']};
`;

const SuggestedSkills = ({ errorMessage }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const { watch } = useFormContext();
  const [
    selectedSkills = [],
    jobPostSkills = [],
    scorecardSkills = [],
    pipelineSkills = [],
  ] = watch(['skills', 'jobPostSkills', 'scorecardSkills', 'pipelineSkills']);

  const hasError = Boolean(errorMessage);

  useEffect(() => {
    if (hasError) {
      setIsOpen(true);
    }
  }, [hasError]);

  if (
    !hasError &&
    !jobPostSkills.length &&
    !scorecardSkills.length &&
    !pipelineSkills.length
  ) {
    return null;
  }

  const normalizeSkillName = (name: string) =>
    name.toLowerCase().replace(/[\s._\-/]/g, '');

  const remainingSkills = (skills: Skill[]) => {
    const skillNames = selectedSkills.map((skill: Skill) =>
      normalizeSkillName(skill.name)
    );
    return skills.filter(
      (skill: Skill) => !skillNames.includes(normalizeSkillName(skill.name))
    );
  };

  const remainingJobPostSkills = remainingSkills(jobPostSkills);
  const remainingScorecardSkills = remainingSkills(scorecardSkills);
  const remainingPipelineSkills = remainingSkills(pipelineSkills);

  if (
    !hasError &&
    !remainingJobPostSkills.length &&
    !remainingScorecardSkills.length &&
    !remainingPipelineSkills.length
  ) {
    return null;
  }

  return (
    <StyledContainer filled>
      <ClickableStack
        direction="row"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Body medium>{t('add_suggestions')}</Body>
        {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
      </ClickableStack>
      <Collapsible isOpen={isOpen} id="suggested-skills">
        <Stack spacing={2} sx={{ marginTop: 0.5 }}>
          {hasError && <ErrorText role="alert">{errorMessage}</ErrorText>}

          <SkillButtons
            remainingSkills={remainingJobPostSkills}
            label={t('job_description')}
          />

          <SkillButtons
            remainingSkills={remainingScorecardSkills}
            label={t('scorecard_attributes')}
          />

          <SkillButtons
            remainingSkills={remainingPipelineSkills}
            label={t('add_pipeline_skills_suggestions')}
          />
        </Stack>
      </Collapsible>
    </StyledContainer>
  );
};

export default SuggestedSkills;
